import { RoomsService } from "@/api/rooms";
import { useQuery } from "@tanstack/react-query";
import type { Room } from "@/api/rooms/types";
import { queryOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./constants";

interface UseRoomsOptions {
  enabled?: boolean;
}

export const useGymRooms = (
  id: number | undefined,
  options?: UseRoomsOptions,
) => {
  return useQuery<{ gymRooms: Room[] }, Error>({
    queryKey: queryKeys.rooms.list(id!),
    queryFn: () => RoomsService.instance.getRooms(id!),
    enabled: options?.enabled ?? !!id,
  });
};

export const roomQueryOptions = (gymId: string, id: string) =>
  queryOptions({
    queryKey: queryKeys.rooms.detail(id),
    queryFn: () => RoomsService.instance.getRoom(gymId, id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useGymRoom = (
  gymId: string,
  roomId: string,
  options: UseRoomsOptions = {},
) => {
  const { enabled = true } = options;

  return useQuery({
    ...roomQueryOptions(gymId, roomId),
    enabled: enabled && !!gymId && !!roomId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useDeleteGymRooms = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { gymId: string; gymRoomsIds: number[] }>({
    mutationFn: ({ gymId, gymRoomsIds }) =>
      RoomsService.instance.deleteRooms(gymId, gymRoomsIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rooms.list(Number(variables.gymId)),
      });
    },
  });
};

export type UpdateGymRoomData = Partial<{
  name: string;
  description: string;
  maximumCapacity: number | null;
  equipment: Record<string, number>;
}>;

export const useUpdateGymRoom = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Room,
    Error,
    { gymId: string; id: string; data: UpdateGymRoomData }
  >({
    mutationFn: ({ gymId, id, data }) =>
      RoomsService.instance.updateRoom(gymId, id, data),

    onSuccess: (updatedRoom, { id }) => {
      queryClient.setQueryData(queryKeys.rooms.detail(id), updatedRoom);
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
    },
  });
};

export const useCreateGymRoom = () => {
  const queryClient = useQueryClient();

  return useMutation<Room, Error, { gymId: string; data: UpdateGymRoomData }>({
    mutationFn: ({ gymId, data }) =>
      RoomsService.instance.createRoom(gymId, data),

    onSuccess: (createdRoom) => {
      queryClient.setQueryData(
        queryKeys.rooms.detail(String(createdRoom.id)),
        createdRoom,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
    },
  });
};
